import { Children, ReactNode, useLayoutEffect, useRef } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import LazyImage from "@components/LazyImage/LazyImage";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import "./ProfileSidebar.scss";

export interface ProfileSidebarImage {
    src: string;
    alt: string;
    /** Shown while the image loads and when it fails to load. */
    placeholder?: ReactNode;
}

interface Props {
    title?: ReactNode;
    /** `null` removes the image area entirely, so the content below moves up. */
    image: ProfileSidebarImage | null;
    actions?: ReactNode;
    isCollapsed: boolean;
    isScrolled?: boolean;
    /** Heading pinned above the scrollable content. */
    contentTitle?: ReactNode;
    /** Scrollable related content. The scroll area is omitted when it renders no children (false, null, []). */
    children?: ReactNode;
    imageTestId?: string;
}

/**
 * Sticky profile sidebar shell shared by Category Profile and Food Profile.
 * Pages theme and position it through the `--profile-sidebar-*` CSS custom properties.
 */
const ProfileSidebar: FCX<Props> = ({
    title,
    image,
    actions,
    isCollapsed,
    isScrolled = false,
    contentTitle,
    children,
    imageTestId,
    className,
    style,
    testId,
}) => {
    const sidebarRef = useRef<HTMLElement>(null);
    const pinnedRef = useRef<HTMLDivElement>(null);
    const hasContent = Children.toArray(children).length > 0;

    // The scroll area takes whatever height the pinned block (title, image, actions) leaves free.
    // The height is written straight to the element, not to state: it changes on every frame of the
    // collapse animation, and a state update would re-render the whole sidebar each time.
    useLayoutEffect(() => {
        const sidebar = sidebarRef.current;
        const pinned = pinnedRef.current;
        if (!sidebar || !pinned) return;
        const measure = () => {
            const height = pinned.offsetHeight;
            if (height > 0) sidebar.style.setProperty("--profile-sidebar-pinned-height", `${height}px`);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(pinned);
        return () => observer.disconnect();
    }, []);

    return (
        <aside
            ref={sidebarRef}
            className={cn(
                "ProfileSidebar",
                className,
                isCollapsed && "is-collapsed",
                isScrolled && "is-scrolled",
                !!contentTitle && "has-content-title",
            )}
            style={style}
            data-testid={testId}
        >
            <div ref={pinnedRef} className="ProfileSidebar__pinned">
                {title && <div className="ProfileSidebar__title">{title}</div>}
                {/* LazyImage keeps its load/error state for its lifetime; keyed by src so a failed
                    placeholder src ("" while loading) can't hide the real image once it arrives. */}
                {image && (
                    <LazyImage
                        key={image.src}
                        className="ProfileSidebar__image"
                        src={image.src}
                        alt={image.alt}
                        placeholder={image.placeholder && (
                            <span className="ProfileSidebar__image-placeholder">{image.placeholder}</span>
                        )}
                        testId={imageTestId}
                    />
                )}
                {actions}
            </div>

            {hasContent && (
                <div className="ProfileSidebar__scroll">
                    {contentTitle && <div className="ProfileSidebar__content-title">{contentTitle}</div>}
                    <CustomBaseScrollbars autoHeight autoHeightMax="var(--profile-sidebar-scroll-max)">
                        <div className="ProfileSidebar__content">{children}</div>
                    </CustomBaseScrollbars>
                </div>
            )}
        </aside>
    );
};

export default ProfileSidebar;
